import * as React from 'react';

import {
  fetchArtifactText,
  isArtifactNotFound,
  openArtifactInNewTab,
  signArtifactUrl,
} from '@modules/_shared/components/chat/artifactApi';
import {
  type ArtifactSummary,
  createRequestGate,
  previewKindOf,
} from '@modules/_shared/components/chat/artifactFiles';
import {
  ArtifactPreviewDialog,
  type HtmlPreviewState,
} from '@modules/_shared/components/chat/ArtifactPreviewDialog';
import { ChatImagePreviewDialog } from '@modules/_shared/components/chat/ChatImagePreviewDialog';
import { useLocale } from '@shared/hooks/locale';

type ImagePreview = {
  artifact: ArtifactSummary;
  src: string;
  retried: boolean;
};

type Options = {
  // The file is gone (deleted, or not this user's): called on every 404, even
  // for a preview the user already moved away from.
  onGone: (artifact: ArtifactSummary) => void;
  // A preview failed while still the open one. `error` is the request error,
  // or null when the image itself would not load.
  onFailure: (artifact: ArtifactSummary, error: unknown) => void;
  onDownload: (artifact: ArtifactSummary) => void;
};

// The one preview of the user's files, shared by the Files view and the chat
// file cards; what a file previews as is previewKindOf (artifactFiles.ts):
// - HTML: the text through GET /artifacts/:id/text, rendered as srcdoc in the
//   sandboxed frame (HtmlPreviewFrame, D14), in a large dialog;
// - image: a fresh inline signed URL in the lightbox, signed again once when
//   it fails to load (expired URL);
// - PDF: a fresh signed URL in a new tab.
// `preview` rejects for an image or a PDF whose URL could not be signed; HTML
// failures are reported through onFailure (or in the dialog) instead.
export const useArtifactPreview = ({
  onGone,
  onFailure,
  onDownload,
}: Options) => {
  const { t } = useLocale();
  const [htmlPreview, setHtmlPreview] = React.useState<
    (HtmlPreviewState & { artifact: ArtifactSummary }) | null
  >(null);
  const [imagePreview, setImagePreview] = React.useState<ImagePreview | null>(
    null,
  );
  const [htmlPreviewGate] = React.useState(createRequestGate);

  // A response only lands while its preview is still the open one: closing
  // the dialog, or previewing another file, supersedes it.
  const previewHtml = async (artifact: ArtifactSummary) => {
    const isCurrent = htmlPreviewGate.start();
    const base = { artifact, name: artifact.name };
    setHtmlPreview({ ...base, kind: 'loading' });
    try {
      const { text, truncated } = await fetchArtifactText(artifact.id);
      if (!isCurrent()) {
        return;
      }
      setHtmlPreview(
        truncated
          ? { ...base, kind: 'too-large' }
          : { ...base, kind: 'ready', html: text },
      );
    } catch (error) {
      if (isArtifactNotFound(error)) {
        // Gone either way, even if the user moved on.
        onGone(artifact);
      }
      if (!isCurrent()) {
        return;
      }
      if (isArtifactNotFound(error)) {
        setHtmlPreview(null);
        onFailure(artifact, error);
      } else {
        // Shown in the dialog only, not again behind it.
        setHtmlPreview({
          ...base,
          kind: 'error',
          message: t('Failed to load the preview'),
        });
      }
    }
  };

  const preview = async (artifact: ArtifactSummary): Promise<void> => {
    switch (previewKindOf(artifact)) {
      case 'html':
        return previewHtml(artifact);
      case 'image': {
        const src = await signArtifactUrl(artifact.id, { download: false });
        setImagePreview({ artifact, src, retried: false });
        return;
      }
      case 'pdf':
        return openArtifactInNewTab(artifact.id);
      case null:
        return;
    }
  };

  const closeHtmlPreview = () => {
    htmlPreviewGate.cancel();
    setHtmlPreview(null);
  };

  // The signed URL may have expired (a dialog left open, a sleeping laptop):
  // sign a new one and retry once, then give up.
  const handleImageError = async () => {
    const current = imagePreview;
    if (current == null) {
      return;
    }
    setImagePreview(null);
    if (current.retried) {
      onFailure(current.artifact, null);
      return;
    }
    try {
      const src = await signArtifactUrl(current.artifact.id, {
        download: false,
      });
      setImagePreview({ ...current, src, retried: true });
    } catch (error) {
      if (isArtifactNotFound(error)) {
        onGone(current.artifact);
      }
      onFailure(current.artifact, null);
    }
  };

  const dialogs = (
    <>
      <ArtifactPreviewDialog
        preview={htmlPreview}
        onClose={closeHtmlPreview}
        onDownload={() => {
          if (htmlPreview != null) {
            onDownload(htmlPreview.artifact);
          }
        }}
      />
      <ChatImagePreviewDialog
        src={imagePreview?.src ?? null}
        alt={imagePreview?.artifact.name}
        onClose={() => setImagePreview(null)}
        onError={() => void handleImageError()}
      />
    </>
  );

  return { preview, dialogs };
};
